import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Seed } from '../../types/idea-machine';
import { Lightbulb, SkipForward, Trash2, Plus, Loader2, AlertCircle, CheckCircle2, Shuffle } from 'lucide-react';
import { seedsService } from '../../services/seeds';
import { ideasService } from '../../services/ideas';

const RANDOM_CATEGORY = 'Random (All Categories)';

export default function IdeaLaunchpad() {
  const { userEmail } = useAuth();
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>(RANDOM_CATEGORY);
  const [currentSeed, setCurrentSeed] = useState<Seed | null>(null);
  const [ideas, setIdeas] = useState<string[]>(['']);
  const [isGenerating, setIsGenerating] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [showCustomSeed, setShowCustomSeed] = useState(false);
  const [customSeedPrompt, setCustomSeedPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [seeds, setSeeds] = useState<Seed[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Get a random seed from a specific category
  const getRandomSeedFromCategory = useCallback((category: string, availableSeeds: Seed[]) => {
    const categorySeeds = availableSeeds.filter(seed => {
      if (category === 'User Created') {
        return seed.isUserCreated && seed.userEmail === userEmail;
      }
      if (category === RANDOM_CATEGORY) {
        return !seed.isUserCreated;
      }
      return seed.category === category && !seed.isUserCreated;
    });

    if (categorySeeds.length > 0) {
      const randomIndex = Math.floor(Math.random() * categorySeeds.length);
      return categorySeeds[randomIndex];
    }
    return null;
  }, [userEmail]);

  // Get a random seed from a random category
  const getRandomSeedFromRandomCategory = useCallback((availableSeeds: Seed[]) => {
    // Get unique categories from available seeds
    const availableCategories = Array.from(new Set(availableSeeds.map(seed => seed.category)));
    if (availableCategories.length === 0) return null;

    // Get a random category
    const randomCategory = availableCategories[Math.floor(Math.random() * availableCategories.length)];
    
    // Get a random seed from that category
    return getRandomSeedFromCategory(randomCategory, availableSeeds);
  }, [getRandomSeedFromCategory]);

  // Load categories and initial random seed
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const fetchedCategories = await seedsService.getCategories();
        // Add Random category at the top
        setCategories([RANDOM_CATEGORY, ...fetchedCategories]);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading categories:', error);
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Subscribe to seeds updates and set initial random seed
  useEffect(() => {
    if (!userEmail) return;

    const unsubscribe = seedsService.subscribeToSeeds((updatedSeeds) => {
      console.log('Received updated seeds:', updatedSeeds);
      setSeeds(updatedSeeds);
      
      // Update categories if needed
      const uniqueCategories = new Set<string>([RANDOM_CATEGORY]);
      updatedSeeds.forEach(seed => {
        if (!seed.isUserCreated) {
          uniqueCategories.add(seed.category);
        }
      });

      if (userEmail && updatedSeeds.some(seed => seed.isUserCreated && seed.userEmail === userEmail)) {
        uniqueCategories.add('User Created');
      }

      setCategories(Array.from(uniqueCategories).sort((a, b) => {
        if (a === RANDOM_CATEGORY) return -1;
        if (b === RANDOM_CATEGORY) return 1;
        return a.localeCompare(b);
      }));

      // Set initial random seed if none is selected
      if (!currentSeed && updatedSeeds.length > 0) {
        const randomSeed = selectedCategory === RANDOM_CATEGORY
          ? getRandomSeedFromRandomCategory(updatedSeeds)
          : getRandomSeedFromCategory(selectedCategory, updatedSeeds);
        if (randomSeed) {
          setCurrentSeed(randomSeed);
        }
      }
    }, userEmail);

    return () => unsubscribe();
  }, [userEmail, currentSeed, selectedCategory, getRandomSeedFromRandomCategory, getRandomSeedFromCategory]);

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(value);
    setIdeas(['']);
    setIsGenerating(false);
    setStartTime(null);
    setShowCustomSeed(false);
    setMessage('');
    setError('');
    
    // Get a random seed based on selection
    const randomSeed = value === RANDOM_CATEGORY
      ? getRandomSeedFromRandomCategory(seeds)
      : getRandomSeedFromCategory(value, seeds);
    setCurrentSeed(randomSeed);
  };

  const handleGenerateIdeas = () => {
    setIsGenerating(true);
    setStartTime(new Date());
    setMessage('');
    setError('');
  };

  const handleSkipSeed = () => {
    // Get a random seed based on current category selection
    const randomSeed = selectedCategory === RANDOM_CATEGORY
      ? getRandomSeedFromRandomCategory(seeds)
      : getRandomSeedFromCategory(selectedCategory, seeds);
    setCurrentSeed(randomSeed);
    setMessage('');
    setError('');
  };

  const handleDeleteSeed = async () => {
    if (!currentSeed?.id || !userEmail) return;

    try {
      await seedsService.deleteSeed(currentSeed.id, userEmail);
      setCurrentSeed(null);
      // Get a new random seed based on current category selection
      const randomSeed = selectedCategory === RANDOM_CATEGORY
        ? getRandomSeedFromRandomCategory(seeds)
        : getRandomSeedFromCategory(selectedCategory, seeds);
      setCurrentSeed(randomSeed);
      setMessage('Seed deleted successfully');
    } catch (error) {
      console.error('Error marking seed as inactive:', error);
      setError('Failed to delete seed');
    }
  };

  const handleCreateCustomSeed = () => {
    setShowCustomSeed(true);
    setCurrentSeed(null);
    setIdeas(['']);
    setIsGenerating(false);
    setMessage('');
    setError('');
  };

  const handleSubmitCustomSeed = async () => {
    if (!customSeedPrompt.trim() || !userEmail) return;

    try {
      const newSeed = await seedsService.addUserSeed(userEmail, customSeedPrompt);
      setCurrentSeed(newSeed);
      setCustomSeedPrompt('');
      setShowCustomSeed(false);
      handleGenerateIdeas();
      setMessage('Custom seed created successfully');
    } catch (error) {
      console.error('Error creating custom seed:', error);
      setError('Failed to create custom seed');
    }
  };

  const handleSubmitIdeas = async () => {
    if (!currentSeed?.id || !userEmail || !startTime) {
      setError('Missing required information for submission');
      return;
    }

    const nonEmptyIdeas = ideas.filter(idea => idea.trim() !== '');
    if (nonEmptyIdeas.length === 0) {
      setError('Please enter at least one idea');
      return;
    }

    setIsSubmitting(true);
    setMessage('');
    setError('');

    try {
      console.log('Starting idea submission...', {
        seedId: currentSeed.id,
        userEmail,
        ideasCount: nonEmptyIdeas.length
      });

      const endTime = new Date();
      const timeSpent = Math.round((endTime.getTime() - startTime.getTime()) / 1000);

      await ideasService.submitIdeas({
        seedId: currentSeed.id,
        userEmail,
        content: nonEmptyIdeas,
        timeSpent
      });

      setMessage('Ideas submitted successfully');
      setCurrentSeed(null);
      setIdeas(['']);
      setIsGenerating(false);
      setStartTime(null);
      
      // Get a new random seed based on current category selection
      const randomSeed = selectedCategory === RANDOM_CATEGORY
        ? getRandomSeedFromRandomCategory(seeds)
        : getRandomSeedFromCategory(selectedCategory, seeds);
      setCurrentSeed(randomSeed);
    } catch (error) {
      console.error('Error in handleSubmitIdeas:', error);
      setError(error instanceof Error ? error.message : 'Failed to submit ideas');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIdeaChange = (index: number, value: string) => {
    const newIdeas = [...ideas];
    newIdeas[index] = value;

    // If typing in the last field and it's not empty, add a new one
    if (index === ideas.length - 1 && value.trim() !== '') {
      newIdeas.push('');
    }

    setIdeas(newIdeas);
  };

  const renderCategoryDisplay = (category: string) => {
    if (category === RANDOM_CATEGORY) {
      return (
        <div className="flex items-center gap-2">
          <Shuffle className="h-4 w-4" />
          {category}
        </div>
      );
    }
    return category;
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900 dark:text-white">Idea Launchpad</h1>

      <Card className="p-6 mb-6 bg-gradient-to-br from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 border dark:border-gray-700 shadow-sm transition-colors">
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            <div className="flex-1 w-full md:w-auto">
              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                <SelectTrigger className="w-full bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:ring-blue-500/20">
                  <SelectValue>
                    {isLoading ? (
                      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading categories...
                      </div>
                    ) : (
                      renderCategoryDisplay(selectedCategory)
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="dark:bg-gray-800 dark:border-gray-700">
                  {categories.map((category) => (
                    <SelectItem
                      key={category}
                      value={category}
                      className="cursor-pointer focus:bg-blue-50 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      {renderCategoryDisplay(category)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              variant="outline"
              onClick={handleCreateCustomSeed}
              className="w-full md:w-auto border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Your Own Seed
            </Button>
          </div>

          {showCustomSeed && (
            <div className="space-y-4 p-4 bg-white dark:bg-gray-800 rounded-xl border border-blue-100 dark:border-gray-700 shadow-sm animate-in fade-in slide-in-from-top-2">
              <textarea
                value={customSeedPrompt}
                onChange={(e) => setCustomSeedPrompt(e.target.value)}
                placeholder="Enter your seed prompt..."
                className="w-full p-4 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500 resize-none transition-all"
                rows={3}
              />
              <div className="flex justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => setShowCustomSeed(false)}
                  className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                >
                  Cancel
                </Button>
                <Button onClick={handleSubmitCustomSeed} className="bg-blue-600 hover:bg-blue-700 text-white">
                  Generate Ideas
                </Button>
              </div>
            </div>
          )}

          {currentSeed && !isGenerating && (
            <div className="space-y-6 animate-in fade-in">
              <div className="p-6 bg-white dark:bg-gray-800/50 rounded-xl border border-blue-100 dark:border-gray-700 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg shrink-0">
                    <Lightbulb className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <p className="text-xl md:text-2xl font-medium text-gray-900 dark:text-white leading-relaxed">
                      {currentSeed.prompt}
                    </p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm font-medium px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                        {currentSeed.category}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleGenerateIdeas}
                  className="bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg transition-all"
                  size="lg"
                >
                  Start Ideating
                </Button>
                <Button
                  variant="outline"
                  onClick={handleSkipSeed}
                  className="border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <SkipForward className="h-4 w-4 mr-2" />
                  Skip
                </Button>
                <Button
                  variant="outline"
                  onClick={handleDeleteSeed}
                  className="border-red-100 dark:border-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          )}

          {isGenerating && (
            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4">
              <div className="p-6 bg-white dark:bg-gray-800 rounded-xl border border-blue-100 dark:border-gray-700 shadow-sm sticky top-0 z-10 backdrop-blur-sm bg-white/90 dark:bg-gray-800/90">
                <div className="flex items-start gap-4">
                  <Lightbulb className="h-6 w-6 text-blue-500 mt-1" />
                  <div>
                    <p className="text-lg md:text-xl font-medium text-gray-900 dark:text-white">{currentSeed?.prompt}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{currentSeed?.category}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {ideas.map((idea, index) => (
                  <div key={index} className="relative group animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${index * 50}ms` }}>
                    <div className="absolute left-[-2rem] top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-bold opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex">
                      {index + 1}
                    </div>
                    <input
                      type="text"
                      value={idea}
                      onChange={(e) => handleIdeaChange(index, e.target.value)}
                      placeholder={`Idea ${index + 1}`}
                      className="w-full p-4 border border-gray-200 dark:border-gray-700 rounded-xl focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 transition-all shadow-sm hover:shadow-md focus:shadow-lg"
                      autoFocus={index === ideas.length - 1}
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-3 pt-4 border-t dark:border-gray-700">
                <Button
                  onClick={handleSubmitIdeas}
                  className="bg-blue-600 hover:bg-blue-700 text-white min-w-[140px]"
                  disabled={isSubmitting}
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Ideas'
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Messages */}
          {message && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-900 rounded-lg animate-in fade-in">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                <CheckCircle2 className="h-5 w-5" />
                <p className="font-medium">{message}</p>
              </div>
            </div>
          )}
          {error && (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900 rounded-lg animate-in fade-in">
              <div className="flex items-center gap-2 text-red-700 dark:text-red-300">
                <AlertCircle className="h-5 w-5" />
                <p className="font-medium">{error}</p>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
}